import { themeGet } from 'styled-system';

import type { Modifier } from '../enums/Modifier';
import theme from './theme';

export const getThemeValue = (key: string) => themeGet(key)({ theme });

export const getThemeColor = (color: string) => themeGet(`colors.${color}`);

export const breakpoint = value => props => `
  @media (min-width: ${typeof value !== 'string' ? props.theme.breakpoints[value] : value})
`;

export const breakpointMinMax = (min: number | string, max: number) => props => {
  const minValue = typeof min !== 'string' ? props.theme.breakpoints[min] : min;
  const [maxValue] = props.theme.breakpoints[max].match(/\d+/g);

  return `
    @media (min-width: ${minValue}) and (max-width: ${maxValue - 1}px)
  `;
};

export const fontSize = value => props => `font-size: ${props.theme.fontSizes[value]}px`;

const MAGIC_LINE_HEIGHT_COEFFICIENT = 1.25;

export const font =
  (size = 0, height = -1) =>
  props => {
    height =
      height === -1
        ? props.theme.space.findIndex(
            spacing => spacing >= props.theme.fontSizes[size] * MAGIC_LINE_HEIGHT_COEFFICIENT
          )
        : height;

    return `
    ${fontSize(size)(props)};
    ${lineHeight(height)(props)}
  `;
  };

const boxModel =
  prefix =>
  (
    top,
    right = top,
    bottom = top,
    left = right
    // eslint-disable-next-line max-params
  ) =>
  props =>
    Object.entries({ top, right, bottom, left })
      .map(
        ([key, value]) =>
          value !== null && `${prefix && `${prefix}-`}${key}: ${space(value)(props)}`
      )
      .filter(Boolean)
      .join(';');

export const absolute = boxModel('');

export const padding = boxModel('padding');

export const margin = boxModel('margin');

/**
 * @deprecated
 * Use themeBoxShadow from system-utilities instead
 */
export const boxShadow = value => props => `box-shadow: ${props.theme.boxShadows[value]}`;

export const space = value => props =>
  typeof props.theme.space[value] !== 'undefined' ? `${props.theme.space[value]}px` : value;

export const lineHeight = value => props => `line-height: ${space(value)(props)}`;

export type ModifiersProps = { modifiers?: Array<Modifier | string> };

export const modifiers = (props: ModifiersProps) => {
  if (!props.modifiers) {
    return '';
  }

  const styles = [];

  props.modifiers.forEach(modifier => {
    const [key, ...rest] = modifier.split('.');
    const value = rest.join('.');

    switch (key) {
      case 'font-style':
      case 'font-weight':
        styles.push(`${key}: ${value} !important`);
        break;
      case 'color':
        const color = value[0] === '#' ? value : getThemeColor(value)(props);
        styles.push(`${key}: ${color} !important`);
        break;
      default:
      // Nothing should happen
    }
  });

  return styles.join(';');
};
